import { Injectable, NgZone, inject } from '@angular/core';
import { Network } from '@capacitor/network';
import { ToolBarType, InAppBrowser, UrlEvent } from '@capgo/capacitor-inappbrowser';
import { Observable, Subject } from 'rxjs';
import { LoggerService } from './logger.service';
import { ConfigService } from './json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { Browser } from '@capacitor/browser';
import { PluginListenerHandle } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class InAppBrowserService {
  private zone = inject(NgZone);
  private loggerService = inject(LoggerService);
  private configService = inject(ConfigService);

  public networkConnected = true;

  private browserClosed$ = new Subject<void>();
  private urlChanged$ = new Subject<string>();

  private readonly serviceListeners: PluginListenerHandle[] = [];
  private activeWebviewId: string | null = null;
  private lifecycle: Promise<unknown> = Promise.resolve();

  public constructor() {
    Network.addListener('networkStatusChange', status => {
      this.zone.run(() => {
        this.networkConnected = status.connected;
      });
    });
  }

  public async open(
    url: string,
    options?: {
      executeScriptAfterLoad: string[],
    }
  ): Promise<void> {
    this.enqueue(() => this.attachListenersAndOpenBrowser(url, options));
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = this.lifecycle.then(task, task);
    this.lifecycle = run.catch(() => undefined);
    return run;
  }

  private async attachListenersAndOpenBrowser(
    url: string,
    options?: { executeScriptAfterLoad: string[] },
  ): Promise<string | null> {
    await this.cleanupListeners();

    try {
      let ownedId: string | null = null;
      this.serviceListeners.push(
        await InAppBrowser.addListener('closeEvent', (event: { id?: string }) => {
          this.zone.run(() => {
            if (ownedId === null || event.id !== ownedId) {
              return;
            }
            this.closeWindow();
          });
        }),
      );

      this.serviceListeners.push(
        await InAppBrowser.addListener('urlChangeEvent', (event: UrlEvent) => {
          this.zone.run(() => {
            if (ownedId !== null && event.id !== undefined && event.id !== ownedId) {
              return;
            }
            this.closeIfWebRedirect(event);
            this.urlChanged$.next(event.url);
          });
        }),
      );

      if (options?.executeScriptAfterLoad) {
        this.serviceListeners.push(
          await InAppBrowser.addListener('browserPageLoaded', (event: { id?: string }) => {
            void (async () => {
              if (ownedId !== null && event.id !== undefined && event.id !== ownedId) {
                return;
              }
              await this.executeScript('document.body.style.zoom = 1.0;');
              for (const inputScript of options.executeScriptAfterLoad) {
                await this.executeScript(inputScript);
              }
            })();
          }),
        );
      }

      const { id } = await InAppBrowser.openWebView({
        url,
        toolbarType: ToolBarType.BLANK,
        visibleTitle: false,
        toolbarColor: '#ffffff',
        // retest this when updating inappbrowser package
        // https://github.com/Cap-go/capacitor-inappbrowser/issues/655
        useTopInset: true,
      });

      ownedId = id;
      this.activeWebviewId = id;
      return id;
    } catch (error: unknown) {
      this.loggerService.error('Failed to open inAppBrowser!', error);
      await this.cleanupListeners();
      return null;
    }
  }

  private async performClose(): Promise<void> {
    const targetId = this.activeWebviewId ?? null;

    await this.cleanupListeners();

    try {
      await InAppBrowser.close({ id: targetId ?? undefined });
    } catch (error: unknown) {
      this.loggerService.error('Error during InAppBrowser.close()', error);
    }
  }

  private closeIfWebRedirect(event: UrlEvent) {
    // if inappbrowser redirects to web app
    // todo - persistent redirection params?
    if (event.url.includes(this.configService.getConfig(EnvironmentKeys.bwmAppLink))) {
      this.close();
    }
    // if inappbrowser redirects to workplace web app
    if (event.url.includes(this.configService.getConfig(EnvironmentKeys.bwmWorkplaceLink))) {
      Browser.open({ url: event.url });
      this.close();
    }
  }

  public close(): Promise<void> {
    return this.enqueue(() => this.performClose());
  }

  public onBrowserClose(): Observable<void> {
    return this.browserClosed$.asObservable();
  }

  public onUrlChange(): Observable<string> {
    return this.urlChanged$.asObservable();
  }
  private closeWindow(): void {
    try {
      this.browserClosed$.next();
    } catch (error) {
      this.loggerService.error('Error during browser close window!', error);
    }
  }

  private async executeScript(code: string) {
    try {
      await InAppBrowser.executeScript({ code, id: this.activeWebviewId ?? undefined });
    } catch (error) {
      this.loggerService.error('Failed to execute afterload script', error);
    }
  }

  public async clearSession(): Promise<void> {
    try {
      await InAppBrowser.clearAllCookies();
      if (this.activeWebviewId !== null) {
        await InAppBrowser.clearCache({ id: this.activeWebviewId });
      }
    } catch (error: unknown) {
      this.loggerService.error('Error during browser session reset!', error);
    }
  }

  private async cleanupListeners(): Promise<void> {
    const currentListeners = this.serviceListeners.splice(0, this.serviceListeners.length);
    await Promise.all(
      currentListeners.map((listener) =>
        listener.remove().catch((error: unknown) => {
          this.loggerService.error('Failed to remove InAppBrowser listener', error);
        }),
      ),
    );
    this.activeWebviewId = null;
  }
}
